import type { Faq } from 'libs/http/api/content/content.types';

import type { EditorialStep } from 'ui/molecules/EditorialStepsSection/EditorialStepsSection.types';
import type { DataFlowChannel } from 'ui/organisms/DataFlowSection/DataFlowSection.types';
import type { PillarCardItem } from 'ui/organisms/PillarCards/PillarCards.types';
import type { StickyRailBlock } from 'ui/organisms/StickyRailSection/StickyRailSection.types';

import type { CapabilityTab } from 'landing-pages/master-data-bulk-feed/sections/CapabilityShowcase/CapabilityShowcase.types';

import styles from './MasterDataManagementPage.module.scss';

type Translate = (key: string) => string;

const MastersTick = () => (
  <svg viewBox="0 0 12 12" fill="none">
    <path d="M2 6.4 4.8 9.2 10 3.2" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const mastersOutcomes = (items: string[]) => (
  <div className={styles.mastersOutcomes}>
    {items.map((item) => (
      <div key={item} className={styles.mastersRow}>
        <span className={styles.mastersTick}>
          <MastersTick />
        </span>
        <span className={styles.mastersRowText}>{item}</span>
      </div>
    ))}
  </div>
);

export const getTrustPillars = (t: Translate): PillarCardItem[] => [
  {
    eyebrow: t('Where it comes from'),
    title: t('Official government registries'),
    description: t(
      'We collect company data directly from 400+ government registries in 200+ countries — never from aggregators or third parties.',
    ),
  },
  {
    eyebrow: t('How you can trust it'),
    title: t('Every value shows its source'),
    description: t(
      'Each field comes with the registry name, a link to the official filing, and the date we retrieved it — ready for any audit.',
    ),
  },
  {
    eyebrow: t('What you can do with it'),
    title: t('Use it in your own products'),
    description: t(
      'Our licensing lets you embed, redistribute, and resell the data — with no proprietary identifiers locking you in.',
    ),
  },
];

export const getAnatomyRoutes = (t: Translate): EditorialStep[] => [
  {
    num: '01',
    title: t('Legal identity'),
    description: t(
      'Registered name, company number, incorporation date, and legal form — exactly as the registry of record holds them, in 200+ jurisdictions.',
    ),
  },
  {
    num: '02',
    title: t('Registered status'),
    description: t(
      'Active, dormant, in liquidation, or dissolved. Status changes surface in the daily sync, so a closed customer never survives another quarter in your CRM.',
    ),
  },
  {
    num: '03',
    title: t('Corporate hierarchy'),
    description: t(
      'Parents, subsidiaries, and ultimate ownership from shareholder filings. Roll revenue up to the group level and de-duplicate accounts across entities.',
    ),
  },
  {
    num: '04',
    title: t('Financials'),
    description: t(
      'Revenue, assets, and employee counts from statutory accounts — figures a company filed under legal obligation, with the filing attached.',
    ),
  },
  {
    num: '05',
    title: t('Officers and ownership'),
    description: t(
      'Directors, secretaries, and shareholders with appointment dates, sourced from officer registers and beneficial ownership filings.',
    ),
  },
  {
    num: '06',
    title: t('Source attribution'),
    description: t(
      'Registry name, direct filing link, and retrieval timestamp on every field. When audit, compliance, or a customer asks where a value came from, the answer is one click.',
    ),
  },
];

export const getMastersSteps = (t: Translate): StickyRailBlock[] => [
  {
    key: 'customer',
    headline: (
      <>
        {t('The same customer lives in')} <em>{t('seven systems')}</em> {t('under seven names.')}
      </>
    ),
    body: (
      <>
        <p className={styles.mastersCost}>
          {t(
            'Duplicate accounts split pipeline across owners, break territory rules, and make group-level revenue impossible to report. Nobody trusts the account list, so nobody acts on it.',
          )}
        </p>

        <div className={styles.mastersChanges}>
          <span className={styles.mastersLabel}>{t('What changes')}</span>
          <p className={styles.mastersChangesText}>
            {t(
              'Every account resolves to a registry identity carrying a group ID — matched on registration number, not on a name someone typed.',
            )}
          </p>
        </div>
      </>
    ),
    content: mastersOutcomes([
      t('Duplicates collapse into one account'),
      t('Subsidiaries roll up to the ultimate parent'),
      t('Dissolved customers leave the forecast'),
    ]),
  },
  {
    key: 'vendor',
    headline: (
      <>
        {t('Duplicate vendors mean')} <em>{t('duplicate payments')}</em>.
      </>
    ),
    body: (
      <>
        <p className={styles.mastersCost}>
          {t(
            'Vendor records get keyed by hand, one regional ERP at a time. The same supplier exists three times under three names, with three sets of payment terms — and stays payable long after it stops trading.',
          )}
        </p>

        <div className={styles.mastersChanges}>
          <span className={styles.mastersLabel}>{t('What changes')}</span>
          <p className={styles.mastersChangesText}>
            {t(
              'Vendors validate against the registry at onboarding: legal name, registration number, VAT, active status. The daily sync watches them from there.',
            )}
          </p>
        </div>
      </>
    ),
    content: mastersOutcomes([
      t('One supplier, one record'),
      t('Liquidation flagged before the payment run'),
      t('VAT verified at source'),
    ]),
  },
  {
    key: 'legal-entity',
    headline: (
      <>
        {t('Hierarchy gaps turn one counterparty into')} <em>{t('two strangers')}</em>.
      </>
    ),
    body: (
      <>
        <p className={styles.mastersCost}>
          {t(
            'Miss the parent link and exposure to the same group gets counted twice, or missed entirely. Corporate structures change constantly, and the entity master rarely keeps up.',
          )}
        </p>

        <div className={styles.mastersChanges}>
          <span className={styles.mastersLabel}>{t('What changes')}</span>
          <p className={styles.mastersChangesText}>
            {t(
              'Entities link to parents, subsidiaries, and ultimate owners from shareholder filings, with a filing link on every relationship.',
            )}
          </p>
        </div>
      </>
    ),
    content: mastersOutcomes([
      t('Group exposure counted once'),
      t('A filing behind every link'),
      t('KYB and screening read one master'),
    ]),
  },
];

export const getCapabilityTabs = (t: Translate): CapabilityTab[] => [
  {
    title: t('Match and de-duplicate'),
    blurb: t(
      'Resolve messy internal records to registry identities using name, address, registration number, VAT, domain, and phonetic variants.',
    ),
    panel: {
      kind: 'diff',
      head: ['', t('Your CRM record'), 'Global Database'],
      rows: [
        { label: t('Legal name'), old: 'barclays plc london', value: 'Barclays PLC' },
        { label: t('Registration no.'), old: 'missing', oldMissing: true, value: '00048839' },
        { label: t('Status'), old: 'unknown', oldMissing: true, value: 'Active' },
        { label: t('Registered address'), old: 'london', value: '1 Churchill Place, London E14 5HP' },
        { label: t('Incorporated'), old: 'missing', oldMissing: true, value: '20 July 1896' },
      ],
      foot: {
        label: t('Match confidence'),
        value: '98%',
        note: t('Merged into one record. Missing fields written back to your CRM.'),
      },
    },
  },
  {
    title: t('Enrich the gaps'),
    blurb: t(
      'Fill missing attributes from the registry of record — status, industry codes, VAT, headcount, financials — field by field.',
    ),
    panel: {
      kind: 'enrich',
      head: ['Cushman & Wakefield DTL Ltd', t('Added from the registry'), t('Source')],
      rows: [
        { label: t('Status'), value: 'Active', cite: 'Companies House · confirmation statement' },
        { label: t('SIC code'), value: '68310 · Real estate agencies', cite: 'Companies House · registered profile' },
        { label: t('VAT number'), value: 'GB649291308', cite: 'HMRC · VAT register' },
        { label: t('Employees'), value: '2,410', cite: 'Companies House · annual accounts' },
        { label: t('Turnover'), value: '£264M', cite: 'Companies House · annual accounts' },
      ],
      foot: { label: t('Fields added'), value: '5', note: t('Every value links to the filing it came from.') },
    },
  },
  {
    title: t('Attach the financials'),
    blurb: t(
      'Pull turnover, assets, and headcount straight from statutory accounts — multi-year, with the filing behind every figure.',
    ),
    panel: {
      kind: 'financials',
      entity: 'Revolut Ltd',
      entitySub: 'Private limited company (Ltd.)',
      status: 'Active',
      source: 'Source: Companies House',
      cols: [t('Figures in £m'), '2020', '2021', '2022', '2023', '2024', t('5-yr trend')],
      rows: [
        { label: t('Turnover'), values: ['222.1', '636.2', '743.9', '1,366.5', '2,452.6'], bars: [3, 7, 8, 14, 26] },
        { label: 'EBITDA', values: ['(119.8)', '75.4', '---', '349.4', '995.4'], bars: [3, 3, 0, 9, 26] },
        {
          label: t('Total liabilities'),
          values: ['4,871.3', '7,554.7', '5,892.3', '6,482.6', '8,534.5'],
          bars: [15, 23, 18, 20, 26],
        },
        {
          label: t('Total assets'),
          values: ['5,288.5', '8,631.8', '6,622.0', '7,386.5', '10,100.4'],
          bars: [14, 22, 17, 19, 26],
        },
        {
          label: t('Net assets'),
          values: ['416.8', '1,076.3', '729.6', '903.9', '1,565.9'],
          bars: [7, 18, 12, 15, 26],
        },
      ],
      foot: { label: t('Years on file'), value: '5', note: t('Filed accounts, as they appear in the product.') },
    },
  },
  {
    title: t('Link the hierarchy'),
    blurb: t(
      'Connect each record to its parents, subsidiaries, and ultimate owner so accounts roll up the way the legal structure actually works.',
    ),
    panel: {
      kind: 'hierarchy',
      head: [t('Group structure'), t('Jurisdiction'), t('Ownership')],
      rows: [
        {
          name: 'Revolut Group Holdings Ltd',
          cc: 'GB',
          juris: 'United Kingdom',
          own: t('Ultimate parent'),
          root: true,
        },
        { name: 'Revolut Ltd', cc: 'GB', juris: 'United Kingdom', own: '100%' },
        { name: 'Revolut NewCo UK Ltd', cc: 'GB', juris: 'United Kingdom', own: '100%' },
        { name: 'Revolut Bank UAB', cc: 'LT', juris: 'Lithuania', own: '100%' },
        { name: 'Revolut Payments UAB', cc: 'LT', juris: 'Lithuania', own: '100%' },
        { name: 'Revolut Technologies Inc', cc: 'US', juris: 'United States', own: '100%' },
      ],
      foot: { label: t('Entities linked'), value: '6', note: t('Every relationship taken from a shareholder filing.') },
    },
  },
  {
    title: t('Maintain in perpetuity'),
    blurb: t(
      'Registries change every day. The daily sync pushes status changes, address moves, and new filings into your master record automatically.',
    ),
    panel: {
      kind: 'changelog',
      head: [t('Date'), t('Entity'), t('Change detected')],
      rows: [
        { date: '16 Jul', entity: 'Meridian Trade Ltd', was: 'Active', now: t('In Liquidation'), bad: true },
        { date: '15 Jul', entity: 'Barclays PLC', was: 'FY2023 accounts', now: t('FY2024 accounts filed') },
        { date: '15 Jul', entity: 'Revolut Ltd', was: t('Registered address'), now: t('Updated from filing') },
        { date: '14 Jul', entity: 'Revolut Group Holdings', was: t('5 subsidiaries'), now: t('6 · new entity added') },
      ],
      foot: {
        label: t('Changes this week'),
        value: '4',
        note: t('Written into your master records with the next daily sync.'),
      },
    },
  },
];

export const getDeliveryChannels = (t: Translate): DataFlowChannel[] => [
  {
    title: 'REST API',
    description: t('Match, enrich, and monitor endpoints with instant responses at the point of entry.'),
    link: { label: t('Get API keys'), href: '/book-a-demo' },
  },
  {
    title: t('Batch files'),
    description: t(
      'Send a file over SFTP or cloud storage, receive it back matched and de-duplicated. CSV, JSON, or Parquet.',
    ),
    link: { label: t('See file specs'), href: '/book-a-demo' },
  },
  {
    title: t('CRM sync'),
    description: t(
      'Native write-back into Salesforce, HubSpot, and Microsoft Dynamics — enriched records where teams work.',
    ),
    link: { label: t('See integrations'), href: '/crm' },
  },
  {
    title: t('Warehouse'),
    description: t(
      'Delivery into Snowflake or BigQuery for the teams that model downstream. Daily delta or full load.',
    ),
    link: { label: t('Talk to data sales'), href: '/book-a-demo' },
  },
  {
    title: 'MCP server',
    description: t('Point your AI assistant at your golden records and get the registry answer back, filing attached.'),
    link: { label: t('Add to Claude'), href: 'https://claude.ai/new#directory/connectors/global-database' },
  },
];

export const getFaqItems = (t: Translate): Faq[] => [
  {
    id: 'source',
    question: t('Where does the data actually come from?'),
    answer: t(
      'Directly from more than 400 government registries across 200+ countries — company registers, tax authorities, and beneficial-ownership filings. <b>No aggregators sit between the source and your record</b>, so nothing reaches you second-hand.',
    ),
    default_opened: true,
  },
  {
    id: 'vs-dnb',
    question: t('How is this different from Dun & Bradstreet?'),
    answer: t(
      'Two things. Every field carries its source — registry name, filing link, retrieval timestamp — so you can audit any value. And the records use <b>open registry identifiers</b> you own outright, rather than a proprietary number you rent and can never take with you.',
    ),
  },
  {
    id: 'match-rate',
    question: t('What match rate should we expect?'),
    answer: t(
      "It depends on your data, so we won't quote a number blind. Send a representative export and we'll return the actual rate against your file — matched entities, duplicates found, and the fields we can fill — before you commit to anything.",
    ),
  },
  {
    id: 'refresh',
    question: t('How often is the data refreshed?'),
    answer: t(
      'Matched entities are monitored at their source registry and changes flow through a daily sync — status changes, address moves, new filings, and hierarchy updates land in your master record automatically.',
    ),
  },
  {
    id: 'replace-mdm',
    question: t('Do we have to replace our MDM platform?'),
    answer: t(
      'No. Global Database is the reference layer, not a platform. It feeds Informatica, Reltio, SAP MDG, or an in-house stack through connectors, files, and APIs — nothing you run today has to be rebuilt.',
    ),
  },
  {
    id: 'resell',
    question: t('Can we resell or embed the data in our own product?'),
    answer: t(
      "Yes. Licensing includes reseller and redistribution rights, so you can embed and resell the data downstream. Because identifiers are open, your customers aren't locked into a proprietary scheme either.",
    ),
  },
  {
    id: 'hierarchies',
    question: t('How do you handle corporate hierarchies?'),
    answer: t(
      'Parents, subsidiaries, and ultimate owners are linked from shareholder filings, with a filing reference on every relationship. Accounts roll up to the group the way the legal structure actually works — not a modelled guess.',
    ),
  },
  {
    id: 'estimates',
    question: t('Are estimated figures labelled?'),
    answer: t(
      "Yes. Filed figures come straight from statutory accounts with the filing attached. Where a value is modelled — estimated revenue or headcount, for example — it's <b>labelled as modelled</b>, never presented as filed.",
    ),
  },
  {
    id: 'delivery',
    question: t('How is the data delivered?'),
    answer: t(
      'REST API for point-of-entry resolution, batch files over SFTP or cloud storage, native write-back to Salesforce, HubSpot, and Dynamics, delivery into Snowflake or BigQuery, and an MCP server for Claude, ChatGPT, and Cursor.',
    ),
  },
  {
    id: 'file-privacy',
    question: t('Is our uploaded file kept or shared?'),
    answer: t(
      'Files sent for a match test are processed under NDA and deleted after the test. They are never added to the database or shared.',
    ),
  },
];
